import React from 'react'
import { useLocation } from 'react-router-dom'

import {
  buttonStyles,
  chevronStyle,
  linkStyles,
  linkWrapperStyles,
  mainNavStyles,
  mobileIconStyle,
} from './styles'
import { Icon, IconSize } from 'sidecar-components/atoms/Icon'
import { Menu } from 'sidecar-components/molecules/Menu'
import { Link, LinkType } from 'sidecar-components/atoms/Link'
import { MainNavProps } from './MainNav.types'

/**
 * The main navigation section for our global nav. This also includes the
 * dropdown menu for the account section. This is the only global nav visible on
 * mobile.
 *
 * @returns An HTML <nav> element
 */
export const MainNav = ({ navItems = [], sentiment }: MainNavProps) => {
  // because sometimes the path is /account/blah, we need to get the root
  // path to determine if the link should show as selected or not.
  const { pathname = '/' } = useLocation()
  const currentRoot = `/${pathname.split('/')[1]}`

  return (
    <nav className={mainNavStyles}>
      {/* The main nav items are generated in a loop, except for account
          because it has the dropdown/menu thingy. */}
      {navItems.map(item => {
        // having more than one selected path option means that we can make
        // sure that links like account are selected for both /account and
        // /cards (for example).
        const isSelected = item.selectedPaths?.includes(currentRoot)

        const linkContent = (
          <>
            <Icon
              name={item.mobileIcon}
              className={mobileIconStyle({ isSelected, sentiment })}
            />
            <span>{item.title}</span>

            {/* the account link needs to display a chevron on larger screens */}
            {!item.path && (
              <Icon
                name="chevronDown"
                className={chevronStyle(sentiment)}
                size={IconSize.SM}
              />
            )}
          </>
        )

        return (
          <div
            data-testid={`btn_navItem_${item.mobileIcon}`}
            key={item.path || `menu-${item.mobileIcon}-${item.title}`}
            className={`${linkWrapperStyles} ${item.className}`}
          >
            {/* If there is a path provided, the button should go to that page.
            If there isn't one (ex: the account link), we can assume it's a menu */}
            {!item.path ? (
              <Menu
                sections={item.menuItems ?? []}
                aria-labelledby="accountMenu"
                trigger={
                  <button
                    className={buttonStyles({ isSelected, sentiment })}
                    data-testid="btn_accountMenuItem"
                  >
                    {linkContent}
                  </button>
                }
              />
            ) : item.crossApp ? (
              // Cross-MFE links render as a plain same-tab anchor so the proxy
              // can route the request to the target app instead of letting
              // react-router-dom intercept it client-side.
              <a
                href={item.path}
                className={linkStyles({ isSelected, sentiment })}
              >
                {linkContent}
              </a>
            ) : (
              <Link
                className={linkStyles({ isSelected, sentiment })}
                path={item.path}
                type={LinkType.INTERNAL}
              >
                {linkContent}
              </Link>
            )}
          </div>
        )
      })}
    </nav>
  )
}
